import { useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "../root/ConfigurationContext";
import lockup from "../root/logos/sliedrecht-sport-sponsoring.svg";

/**
 * Login page: the colour lockup on top and the e-mail/password form below it
 * in a white card, centred on a light background. There is no sign-up and no
 * SSO; a new password is handed out by an administrator.
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const { title } = useConfigurationContext();
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "crm.auth.sign_in_failed"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-muted px-4 py-10">
      <img className="w-64 max-w-full" src={lockup} alt={title} />
      <div className="w-full max-w-md space-y-6 rounded-2xl bg-white p-8">
        <h1 className="text-center text-2xl font-semibold tracking-tight text-brand-navy">
          {translate("ra.auth.sign_in")}
        </h1>
        <Form className="space-y-6" onSubmit={handleSubmit}>
          <TextInput
            label="ra.auth.email"
            source="email"
            type="email"
            autoComplete="username"
            validate={required()}
          />
          <TextInput
            label="ra.auth.password"
            source="password"
            type="password"
            autoComplete="current-password"
            validate={required()}
          />
          <Button
            type="submit"
            className="w-full cursor-pointer"
            disabled={loading}
          >
            {translate("ra.auth.sign_in")}
          </Button>
        </Form>
        <p className="text-sm text-center text-muted-foreground">
          {translate("crm.auth.forgot_password_hint")}
        </p>
      </div>
      <Notification />
    </div>
  );
};
